import { useSelector } from "react-redux";
import { ROUTES } from "routes/routePaths";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { AiOutlineRight } from "react-icons/ai";
import disabledTerminalImg from "assets/images/disabled-terminal.svg";
import { formatAmount, getUniqueTransactionTypes } from "utils/helper";
import terminalRequestImg from "assets/images/terminal-request-img.svg";
import terminalSuccessImg from "assets/images/terminal-success-rate.png";
import transactionVolumeImg from "assets/images/transaction-volume.png";
import totalAmountSettledImg from "assets/images/total-amount-settled.svg";
import totalAssignedTerminalsImg from "assets/images/total-assigned-terminals.svg";
import {
  getAllData,
  setTransactionDates,
  getTerminalSummary,
  getTerminalActivity,
  setSelectedTransactionTypes,
} from "redux/actions/dashboard/terminalDashboardActions";
import {
  Typography,
  Carousel,
  DateCTA,
  LoadingSkeleton,
  MultiSelect,
  NewDateFIlterModal as DateFilterModal,
} from "components";
import { permissions } from "utils/permissions";

const SummaryCards = () => {
  const navigate = useNavigate();

  const [openDateModal, setOpenDateModal] = useState(false);

  const { dates } = useSelector((state) => ({
    dates: state.terminalDashboardReducer.dates,
  }));

  const { user } = useSelector((state) => state.user);
  const userAuthorities = user?.authorities;

  const handleModalClose = () => setOpenDateModal(false);
  const getAgentsData = () => {
    getAllData();
    handleModalClose();
  };

  useEffect(() => {
    getTerminalSummary();
    getTerminalActivity();
  }, []);

  const { manageTerminals } = ROUTES;

  const { transactionTypes, selectedTransactionTypes } = useSelector(
    (state) => ({
      transactionTypes: state.transactionsReducer,
      selectedTransactionTypes:
        state.terminalDashboardReducer.selectedTransactionTypes,
    })
  );

  const { terminalActivity, terminalSummary, transactionTypeStats } =
    useSelector((state) => state.terminalDashboardReducer);

  const {
    enabled,
    disabled,
    pendingCreationApproval,
    pendingIssuingApproval,
    pendingSwapApproval,
    rejectedApproval,
    unissued,
  } = terminalSummary.data;

  const { totalTransVolume, totalTransValue } =
    transactionTypeStats?.data || {};

  const { active, inactive, totalTerminal, successRate } =
    terminalActivity.data;

  let terminalAndUsers = [
    {
      label: "Total Terminals",
      value: totalTerminal,
      link: manageTerminals,
      canView: permissions.canSeeManageTerminals,
      loading: totalTerminal === undefined,
      image: totalAssignedTerminalsImg,
      valueState: "success",
      imgWidth: "30px",
    },
    {
      label: "Terminal Success Rate",
      value: `${successRate || 0}%`,
      image: terminalSuccessImg,
      loading: terminalActivity.loading,
      valueState: "success",
    },
    {
      label: "Enabled Terminals",
      value: enabled,
      image: totalAssignedTerminalsImg,
      loading: terminalSummary.loading,
      valueState: "success",
      imgWidth: "30px",
    },
    {
      label: "Disabled Terminals",
      value: disabled,
      image: disabledTerminalImg,
      loading: terminalSummary.loading,
      imgWidth: "50px",
    },
    {
      label: "Unissued Terminals",
      value: unissued,
      image: disabledTerminalImg,
      loading: terminalSummary.loading,
      imgWidth: "50px",
    },
  ];

  if (user?.roles.includes("PM Approver"))
    terminalAndUsers = [
      ...terminalAndUsers,
      {
        label: "Pending Creation Approval",
        value: pendingCreationApproval,
        image: terminalRequestImg,
        valueState: "success",
        loading: terminalSummary.loading,
      },
      {
        label: "Pending Issuing Approval",
        value: pendingIssuingApproval,
        image: terminalRequestImg,
        valueState: "success",
        loading: terminalSummary.loading,
      },
      {
        label: "Pending Swap Approval",
        value: pendingSwapApproval,
        image: terminalRequestImg,
        valueState: "success",
        loading: terminalSummary.loading,
      },
      {
        label: "Rejected Approval",
        value: rejectedApproval,
        image: terminalRequestImg,
        loading: terminalSummary.loading,
      },
    ];

  return (
    <>
      <div>
        <Carousel showArrows={terminalAndUsers.length > 5}>
          {terminalAndUsers.map(
            (
              {
                label,
                value,
                link,
                image,
                valueState,
                imgWidth,
                loading,
                canView,
              },
              key
            ) =>
              loading ? (
                <div className=" pt-3 px-2" key={key}>
                  <LoadingSkeleton height="7rem" width="12rem" key={key} />
                </div>
              ) : (
                <div
                  key={key}
                  className={`admin-stats-card p-2 ${
                    link ? "cursor-pointer" : ""
                  }`}
                  onClick={() =>
                    link && userAuthorities.includes(canView)
                      ? navigate(link)
                      : {}
                  }
                >
                  <Typography variant="caption2" state="primary">
                    {label}
                  </Typography>
                  <div className="main-detail mt-xl-1">
                    <Typography className="mt-1" state={valueState || "grey"}>
                      {value}
                    </Typography>
                    <img
                      src={image}
                      alt={label}
                      style={{ width: imgWidth ? imgWidth : "" }}
                      className={imgWidth ? "min-image" : ""}
                    />
                  </div>
                  {link && userAuthorities.includes(canView) && (
                    <div className="d-flex justify-content-end pr-4 mt-1">
                      <AiOutlineRight color="#3D3333" fontSize={"12px"} />
                    </div>
                  )}
                </div>
              )
          )}
        </Carousel>
      </div>

      <div className="d-flex align-items-center mt-4 cursor-pointer">
        <div className="d-flex align-items-center">
          <Typography variant="caption0">Choose Date:</Typography> &nbsp;
          <DateCTA onClick={() => setOpenDateModal(true)} /> &nbsp; &nbsp;
          {dates.dateFrom && dates.dateTo && (
            <Typography variant="caption2" state="primary">
              Date set: {dates.dateFrom} to {dates.dateTo}
            </Typography>
          )}
        </div>{" "}
        &nbsp; &nbsp; &nbsp;
        {transactionTypes.transactionTypes?.length && (
          <div style={{ width: "fit-content" }}>
            <div
              className="space-top-2 cursor-pointer d-flex align-items-center px-2 mb-2"
              style={{ width: "" }}
            >
              <Typography variant="body3">Transaction Type:</Typography> &nbsp;
              <MultiSelect
                placeholder={"All Transactions"}
                // value={selectedTransactionTypes}
                isSearchable={false}
                borderStyle="1px solid #102E88"
                clearable={false}
                handleChange={(val) => setSelectedTransactionTypes(val)}
                options={getUniqueTransactionTypes(
                  transactionTypes?.transactionTypes
                )?.map(({ type, description }) => ({
                  label: description,
                  value: type,
                }))}
                onBlur={() => getAllData()}
                name="transaction-type-select"
              />
            </div>
            <div
              className="d-flex flex-wrap mb-2"
              style={{ maxWidth: "19rem" }}
            >
              {selectedTransactionTypes.length ===
                getUniqueTransactionTypes(transactionTypes?.transactionTypes)
                  ?.length || !selectedTransactionTypes.length ? (
                <Typography variant="caption0">All Transactions</Typography>
              ) : (
                selectedTransactionTypes.map((transaction, i) => (
                  <Typography variant="caption0" key={i}>
                    {transaction.label}
                    {i === selectedTransactionTypes.length - 1 ? "." : ","}{" "}
                    &nbsp;
                  </Typography>
                ))
              )}
            </div>
          </div>
        )}
      </div>

      <div className="mb-4 transaction-summary">
        <Carousel>
          {transactionTypeStats?.loading ? (
            <div className="pt-3 px-2">
              <LoadingSkeleton height="7rem" />
            </div>
          ) : (
            <div className="admin-stats-card p-2">
              <Typography variant="caption2" state="primary">
                Total Transaction Volume
              </Typography>
              <div className="main-detail mt-xl-2">
                <Typography className="mt-1" state="primary">
                  {formatAmount(totalTransVolume)}
                </Typography>
                <img
                  src={transactionVolumeImg}
                  alt=""
                  style={{ height: 30, width: 30 }}
                />
              </div>
            </div>
          )}

          {transactionTypeStats?.loading ? (
            <div className=" pt-3 px-2">
              <LoadingSkeleton height="7rem" />
            </div>
          ) : (
            <div className="admin-stats-card p-2">
              <Typography variant="caption2" state="primary">
                Total Transaction Value
              </Typography>
              <div className="main-detail mt-xl-2">
                <Typography className="mt-1" state="primary">
                  ₦{formatAmount(totalTransValue)}
                </Typography>
                <img
                  src={totalAmountSettledImg}
                  alt=""
                  style={{ height: 30, width: 30, marginTop: ".1rem" }}
                />
              </div>
            </div>
          )}

          {terminalActivity?.loading ? (
            <div className=" pt-3 px-2">
              <LoadingSkeleton height="7rem" />
            </div>
          ) : (
            <div className="admin-stats-card p-2">
              <Typography variant="caption2" state="primary">
                Actively Transacting Terminals
              </Typography>
              <div className="main-detail mt-xl-1">
                <Typography className="mt-1" state="success">
                  {active}
                </Typography>
                <img
                  src={totalAssignedTerminalsImg}
                  alt=""
                  style={{ width: "30px" }}
                />
              </div>
            </div>
          )}

          {terminalActivity?.loading ? (
            <div className=" pt-3 px-2">
              <LoadingSkeleton height="7rem" />
            </div>
          ) : (
            <div className="admin-stats-card p-2">
              <Typography variant="caption2" state="primary">
                Inactive Terminals
              </Typography>
              <div className="main-detail mt-xl-1">
                <Typography className="mt-1" state="success">
                  {inactive}
                </Typography>
                <img
                  src={totalAssignedTerminalsImg}
                  alt=""
                  style={{ width: "30px" }}
                />
              </div>
            </div>
          )}
        </Carousel>
      </div>
      <DateFilterModal
        dateFrom={dates.dateFrom}
        dateTo={dates.dateTo}
        openDateModal={openDateModal}
        onClose={handleModalClose}
        handleFromChange={(val) => setTransactionDates(val, "dateFrom")}
        handleToChange={(val) => setTransactionDates(val, "dateTo")}
        onButtonClick={getAgentsData}
      />
    </>
  );
};

export default SummaryCards;
